import {
  Button,
  Table,
  TableColumn,
  TableParams,
  TableRowKey,
} from "@datapay-by-datacom/design-system";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserGroup } from "../../../generated/core-api";
import {
  useCreateUserGroup,
  useDeleteUserGroup,
  useListUserGroups,
  useUserManagementPermissions,
} from "../../hooks";
import { CreateGroupModal } from "../CreateGroupModal";
import s from "./UserGroupTable.module.css";

const createColumns = (
  canEdit: boolean,
  onDelete: (group: UserGroup) => void,
  onView: (group: UserGroup) => void,
  deletingGroupId: string | null,
): TableColumn<UserGroup>[] => {
  const columns: TableColumn<UserGroup>[] = [
    {
      key: "name",
      header: () => "Name",
      cell: (row) => row.name,
    },
    {
      key: "description",
      header: () => "Description",
      cell: (row) =>
        row.description ? (
          <span className={s.groupDescription}>{row.description}</span>
        ) : (
          <span className={s.noDescription}>-</span>
        ),
    },
    {
      key: "created_at",
      header: () => "Created",
      cell: (row) => new Date(row.created_at).toLocaleDateString(),
    },
    {
      key: "actions" as keyof UserGroup,
      header: () => "Actions",
      cell: (row) => (
        <div className={s.actions}>
          <Button variant="secondary" onPress={() => onView(row)}>
            View
          </Button>
          {canEdit && (
            <Button
              variant="destructive"
              onPress={() => onDelete(row)}
              isDisabled={deletingGroupId === row.group_id}
            >
              {deletingGroupId === row.group_id ? "Deleting..." : "Delete"}
            </Button>
          )}
        </div>
      ),
    },
  ];

  return columns;
};

const rowKey: TableRowKey<UserGroup> = (row) => row.group_id;

export function UserGroupTable() {
  const [params, setParams] = useState<TableParams<UserGroup>>({});
  const groupsQuery = useListUserGroups();
  const { canEdit } = useUserManagementPermissions();
  const createMutation = useCreateUserGroup();
  const deleteMutation = useDeleteUserGroup();
  const navigate = useNavigate();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [deletingGroupId, setDeletingGroupId] = useState<string | null>(null);

  const handleDelete = (group: UserGroup) => {
    if (!window.confirm(`Delete group "${group.name}"? This cannot be undone.`)) return;
    setDeletingGroupId(group.group_id);
    deleteMutation.mutate(group.group_id, {
      onSettled: () => setDeletingGroupId(null),
    });
  };

  const handleCreate = (name: string, description?: string) => {
    createMutation.mutate(
      { name, description },
      {
        onSuccess: () => setIsCreateModalOpen(false),
      },
    );
  };

  const handleView = (group: UserGroup) => {
    navigate(`/user-management/groups/${group.group_id}`);
  };

  const columns = createColumns(canEdit, handleDelete, handleView, deletingGroupId);

  return (
    <div className={s.container}>
      {canEdit && (
        <div className={s.header}>
          <Button variant="primary" onPress={() => setIsCreateModalOpen(true)}>
            Create Group
          </Button>
        </div>
      )}
      <Table
        description="User Groups"
        config={{ columns, rowKey }}
        control={{ params, onParamsChange: setParams }}
        query={groupsQuery}
      />
      <CreateGroupModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreate={handleCreate}
        isPending={createMutation.isPending}
      />
    </div>
  );
}
